import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { api } from '../api/client';

function formatFriendCode(raw: string): string {
  const clean = raw.replace(/-/g, '').toUpperCase().slice(0, 6);
  if (clean.length > 2) return `${clean.slice(0, 2)}-${clean.slice(2)}`;
  return clean;
}

interface FriendEntry {
  displayName: string;
  tier: string;
  code: string;
}

export function Friends() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const me = usePlayerStore(s => s.me);
  const [code, setCode] = useState('');
  const [addResult, setAddResult] = useState('');
  const [adding, setAdding] = useState(false);
  const [friends] = useState<FriendEntry[]>([]);

  if (!me) return <Navigate to="/login" replace />;

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(formatFriendCode(e.target.value));
  };

  const handleAdd = async () => {
    if (!code.trim()) return;
    setAdding(true);
    setAddResult('');
    try {
      const res = await api.friend.add({ code: code.trim().toUpperCase() });
      setAddResult(`${res.friend.displayName} (${res.friend.tier})`);
      setCode('');
    } catch (e) {
      setAddResult(`Error: ${String(e)}`);
    } finally {
      setAdding(false);
    }
  };

  const shareUrl = `https://www.panor.tech/stanwars/?friend=${me.friendCode}`;

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Link to="/"><PixelButton variant="ghost" className="text-xs">{t('common.home')}</PixelButton></Link>
        <PixelText size="xl" color="cyan">{t('friends.title')}</PixelText>
      </div>

      {/* Your friend code */}
      <PixelPanel className="text-center">
        <PixelText size="sm" color="yellow">{t('friends.your_code')}</PixelText>
        <PixelText size="2xl" color="pink" className="block mt-2">{me.friendCode}</PixelText>
        <div className="flex gap-2 justify-center mt-3">
          <PixelButton
            variant="ghost"
            className="text-xs"
            onClick={() => void navigator.clipboard.writeText(me.friendCode)}
          >
            {t('friends.copy_code')}
          </PixelButton>
          <PixelButton
            variant="ghost"
            className="text-xs"
            onClick={() => void navigator.clipboard.writeText(shareUrl)}
          >
            {t('friends.copy_link')}
          </PixelButton>
        </div>
      </PixelPanel>

      {/* Add friend */}
      <PixelPanel>
        <PixelText size="base" color="pink" className="mb-2 block">{t('friends.add_friend')}</PixelText>
        <div className="flex gap-2">
          <input
            className="flex-1 bg-ink-800 border-2 border-ink-600 px-3 py-1 pixel-text text-sm text-white uppercase"
            placeholder={t('friends.placeholder')}
            maxLength={7}
            value={code}
            onChange={handleCodeChange}
          />
          <PixelButton onClick={handleAdd} disabled={adding || !code.trim()}>
            {adding ? t('friends.adding') : t('friends.add')}
          </PixelButton>
        </div>
        {addResult && (
          <PixelText size="sm" color="green" className="block mt-2">{addResult}</PixelText>
        )}
      </PixelPanel>

      {/* Friends list */}
      <PixelPanel>
        <PixelText size="base" color="cyan" className="mb-2 block">{t('friends.friends_list')}</PixelText>
        {friends.length === 0 ? (
          <PixelText size="sm" color="white" className="opacity-60">
            {t('friends.no_friends')}
          </PixelText>
        ) : (
          <div className="flex flex-col gap-2">
            {friends.map(f => (
              <div key={f.code} className="flex items-center justify-between">
                <div>
                  <PixelText size="sm" color="white">{f.displayName}</PixelText>
                  <PixelText size="sm" color="cyan" className="block">{f.tier}</PixelText>
                </div>
                <PixelButton
                  variant="ghost"
                  className="text-xs"
                  onClick={() => nav(`/friend/play/${f.code}`)}
                >
                  {t('friends.challenge')}
                </PixelButton>
              </div>
            ))}
          </div>
        )}
      </PixelPanel>

      {/* Match rules footer */}
      <PixelPanel>
        <PixelText size="sm" color="white" className="opacity-60">
          {t('friends.rules_footer')}
        </PixelText>
      </PixelPanel>
    </main>
  );
}
